import * as Api from '@ViewModels';
import { useMutation } from '@tanstack/react-query';
import { useUserSession } from '../../../../models/hooks/appStateHooks';
import { AUTOMATION_TEMPLATE_MUTATION_KEY } from '../../queryKeys';

interface Params {
	templateId: string;
	triggerType: Api.AutomationTriggerType;
	trigger: Api.IAutomationTrigger;
	impersonationContext?: Api.IImpersonationContext;
}

/**
 * Maps AutomationTriggerType enum values to the API path segment expected by the backend.
 * The backend expects simplified trigger path names, not the full enum string values.
 */
function getTriggerPathForType(triggerType: Api.AutomationTriggerType): string | null {
	switch (triggerType) {
		case Api.AutomationTriggerType.Tag:
			return 'Tag';
		case Api.AutomationTriggerType.NewLead:
			return 'NewLead';
		case Api.AutomationTriggerType.NewPolicyTrigger:
		case Api.AutomationTriggerType.ResourceSelector:
			return 'ResourceSelector';
		case Api.AutomationTriggerType.NewClient:
			return 'NewClient';
		case Api.AutomationTriggerType.NewDonor:
			return 'NewDonor';
		case Api.AutomationTriggerType.NewDonationTrigger:
			return 'Donation';
		case Api.AutomationTriggerType.Texting:
			return 'TextingCampaign';
		case Api.AutomationTriggerType.Meeting:
			return 'MeetingReminder';
		case Api.AutomationTriggerType.EventRegistration:
			return 'EventRegistration';
		default:
			return null;
	}
}

export function useAddAutomationTriggerMutation({
	onSuccess,
	onError,
}: {
	onSuccess?: (data: Api.IAutomationTemplate, variables: Params) => void;
	onError?: (error: Api.IOperationResultNoValue, variables: Params) => void;
} = {}) {
	const userSession = useUserSession();
	return useMutation({
		mutationKey: AUTOMATION_TEMPLATE_MUTATION_KEY,
		mutationFn: ({ templateId, triggerType, trigger, impersonationContext }: Params) => {
			const triggerPath = getTriggerPathForType(triggerType);
			if (!triggerPath) {
				return Promise.reject(new Error(`Unsupported trigger type: ${triggerType}`));
			}
			return userSession.webServiceHelper.callAsync<Api.IAutomationTemplate>(
				Api.ImpersonationBroker.composeApiUrl({
					urlPath: `automationTemplate/${encodeURIComponent(templateId)}/trigger/${triggerPath}`,
					impersonationContext,
				}),
				'PUT',
				trigger
			);
		},
		onError,
		onSuccess,
	});
}
